import React, { ForwardedRef, MutableRefObject, useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { MultipleInputList } from './MultipleInputList'
import { PopoverInput } from '@musinsa/layer-components-desktop'

const MAX_MULTI_INPUT_SIZE = 500

interface MultipleInputProps {
  value: string[]
  autoFocus?: boolean
  type?: 'string' | 'number'
  label?: string
  maxSize?: number
  hasNoLimit?: boolean
  placeholder?: string
  isOnPanel?: boolean
  required?: boolean
  disabled?: boolean
  onChange: (values: string[]) => void
  className?: string
}

function MultipleInputInnerV2(props: MultipleInputProps, inputRef: ForwardedRef<HTMLInputElement>) {
  const {
    type = 'string',
    label,
    autoFocus = false,
    maxSize = MAX_MULTI_INPUT_SIZE,
    hasNoLimit = false,
    value = [],
    placeholder = '입력',
    isOnPanel,
    required,
    disabled = false,
    onChange,
    className,
  } = props

  const getFormattedValues = useCallback(
    (values: string[]) => {
      return values
        .map((text) => {
          return String(text).trim()
        })
        .slice(0, maxSize)
    },
    [maxSize],
  )

  const [isOpen, setIsOpen] = useState(false)
  const [rawValue, setRawValue] = useState(Array.isArray(value) ? value.join(',') : value)

  const hasRawValue = Boolean(rawValue)
  const selectedValues = getFormattedValues(value)

  const $input = useRef<HTMLInputElement>(null)
  const isFocused = useRef(autoFocus)

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  const onSyncValue = useCallback(
    (value: string[]) => {
      if (Array.isArray(value) && value.join(',') !== rawValue) {
        setRawValue(value.join(','))
      }
    },
    [rawValue],
  )

  const onChangeValue = (list: string[], syncRaw = true) => {
    if (!list.length || (list.length === 1 && !list[0])) {
      onChange([])

      if (syncRaw) {
        setRawValue('')
      }
    } else {
      const formattedList = getFormattedValues(list)

      onChange(formattedList)

      if (syncRaw) {
        setRawValue(formattedList.join(','))
      }
    }
  }

  useEffect(() => {
    if ($input.current && inputRef) {
      ;(inputRef as MutableRefObject<HTMLInputElement>).current = $input.current
    }
  }, [$input, inputRef])

  useEffect(() => {
    if (!isFocused.current) {
      onSyncValue(value)
    }
  }, [value, onSyncValue])

  const displayValue = hasRawValue ? `${selectedValues[0]}${selectedValues.length > 1 ? ` 외 ${selectedValues.length - 1}개` : ''}` : undefined

  if (isOnPanel) {
    return (
      <MultipleInputList
        type={type}
        value={rawValue}
        maxSize={maxSize}
        hasNoLimit={hasNoLimit}
        onClose={handleClose}
        onChangePanelValue={onChangeValue}
        required={required}
        isOpen
        isOnPanel
      />
    )
  }

  return (
    <Container className={className}>
      <PopoverInput.Root open={isOpen} setOpen={setIsOpen}>
        <PopoverInput.Trigger
          floatingBlockLabel={label}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          displayValue={displayValue}
        />
        <PopoverInput.Content width="100%">
          <MultipleInputList
            type={type}
            value={rawValue}
            maxSize={maxSize}
            hasNoLimit={hasNoLimit}
            onApply={onChangeValue}
            onClose={handleClose}
            isOpen={isOpen}
            required={required}
          />
        </PopoverInput.Content>
      </PopoverInput.Root>
    </Container>
  )
}

const Container = styled.div`
  position: relative;
  width: 100%;
  min-width: 120px;
`

export const MultipleInputV2 = React.forwardRef(MultipleInputInnerV2) as (
  props: MultipleInputProps & { ref?: React.ForwardedRef<HTMLInputElement> },
) => ReturnType<typeof MultipleInputInnerV2>

export default MultipleInputV2
